import { useEffect } from 'react';
import { CLIENT, CLIENT_ANALYST, CORPORATE_CLIENT } from '@globalConstants';
import { useRoles, useUser } from '@hooks';

declare global {
  interface Window {
    hj?: ((...args: unknown[]) => void) & { q?: unknown[] };
    _hjSettings?: { hjid: number; hjsv: number };
  }
}

const HOTJAR_ROLES = [CLIENT, CORPORATE_CLIENT, CLIENT_ANALYST];
const HOTJAR_SCRIPT_ID = 'hotjar-script';

/**
 * Hotjar script for tracking user behavior.
 * Mount once at app level — do not place inside per-screen layouts.
 */
const HotjarScript = () => {
  const { user } = useUser();
  const { validateRoles } = useRoles();
  const shouldTrack = Boolean(user) && validateRoles(HOTJAR_ROLES);

  useEffect(() => {
    if (!shouldTrack || typeof window === 'undefined') return;
    if (window._hjSettings || document.getElementById(HOTJAR_SCRIPT_ID)) return;

    window.hj =
      window.hj ||
      Object.assign(
        (...args: unknown[]) => {
          (window.hj!.q = window.hj!.q || []).push(args);
        },
        { q: [] as unknown[] }
      );

    window._hjSettings = { hjid: 3896836, hjsv: 6 };

    const script = document.createElement('script');
    script.id = HOTJAR_SCRIPT_ID;
    script.async = true;
    script.src = `https://static.hotjar.com/c/hotjar-${window._hjSettings.hjid}.js?sv=${window._hjSettings.hjsv}`;
    document.head.appendChild(script);

    // Intentionally no cleanup: removing Hotjar on route/layout remounts desyncs the DOM and crashes React.
  }, [shouldTrack]);

  return null;
};

export default HotjarScript;
